/** @jsxImportSource @emotion/react */
import React, { memo, useCallback, useMemo, useState } from 'react'
import isValidEmail from '@signal/utils/isValidEmail'
import {
  getImpersonationUrl,
  getUnimpersonationUrl
} from '@signal/core/auth/auth'
import ForwardIcon from '../../Icons/Forward'
import CancelIcon from '../../Icons/Cancel'
import { DeleteOutline } from '@material-ui/icons'
import PureInput from '@signal/components/PureInput'
import useImpersonationHistory from '@signal/core-hooks/useImpersonationHistory'
import withCommonSnapshotsOnly from '@signal/hoc/withCommonSnapshotsOnly'
import {
  ImpersonateButton,
  ImpersonationInputContainer,
  ImpersonationInputWrapper,
  ImpersonationHistoryContainer,
  ImpersonationHistoryList,
  ImpersonationHistoryItem,
  ImpersonationHistoryEmail,
  ImpersonationHistoryDeleteButton
} from './styles'
import { useDarkModeContext } from '@signal/theme/Context/dark-mode'

type ImpersonateUserPropsType = {
  isImpersonating: boolean
  currentUserEmail?: string
}

export const ImpersonateUser: React.FC<ImpersonateUserPropsType> = ({
  isImpersonating,
  currentUserEmail = ''
}) => {
  const [email, setEmail] = useState('')
  const { history, addEmail, removeEmail } = useImpersonationHistory()
  const { isDarkMode } = useDarkModeContext()

  // When impersonating, disable input. When not, validate email.
  const isDisabled = useMemo(
    () => (isImpersonating ? false : !isValidEmail(email)),
    [email, isImpersonating]
  )

  const handleChangeEmail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value)
    },
    [setEmail]
  )

  const handleCallImpersonate = useCallback(() => {
    if (isImpersonating) {
      // If already impersonating, stop impersonation
      const url = getUnimpersonationUrl()
      window.location.replace(url)
    } else {
      // Save email to history before impersonating
      addEmail(email)
      const impersonateUrl = getImpersonationUrl(email)
      window.location.replace(impersonateUrl)
    }
  }, [email, addEmail, isImpersonating])

  const handleEnterPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (!isDisabled || isImpersonating) {
          handleCallImpersonate()
        }
      }
    },
    [isDisabled, handleCallImpersonate, isImpersonating]
  )

  const handleHistoryItemClick = useCallback(
    (historyEmail: string) => {
      setEmail(historyEmail)
    },
    [setEmail]
  )

  const handleSelectHistoryItem = useCallback(
    (historyEmail: string) => () => {
      if (!isImpersonating) {
        handleHistoryItemClick(historyEmail)
      }
    },
    [handleHistoryItemClick, isImpersonating]
  )

  const isHistoryItemSelected = useMemo(
    () => (historyEmail: string) =>
      (!isImpersonating && email === historyEmail) ||
      (isImpersonating && currentUserEmail === historyEmail),
    [isImpersonating, email, currentUserEmail]
  )

  const handleRemoveHistoryItem = useCallback(
    (historyEmail: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      removeEmail(historyEmail)
    },
    [removeEmail]
  )

  return (
    <>
      <ImpersonationInputContainer>
        <ImpersonationInputWrapper>
          <PureInput
            placeholder={
              isImpersonating ? 'Currently impersonating' : 'Impersonate user'
            }
            value={isImpersonating ? currentUserEmail : email}
            onKeyPress={handleEnterPress}
            onChange={handleChangeEmail}
            disabled={isImpersonating}
          />
        </ImpersonationInputWrapper>
        <ImpersonateButton
          $disabled={isImpersonating ? false : isDisabled}
          $isImpersonating={isImpersonating}
          $isDarkMode={isDarkMode}
          onClick={handleCallImpersonate}
        >
          {isImpersonating ? <CancelIcon /> : <ForwardIcon />}
        </ImpersonateButton>
      </ImpersonationInputContainer>

      {/* Impersonation History */}
      {history.length > 0 && (
        <ImpersonationHistoryContainer>
          <ImpersonationHistoryList>
            {history.map((historyEmail: string, index: number) => (
              <ImpersonationHistoryItem
                key={index}
                onClick={handleSelectHistoryItem(historyEmail)}
                $isSelected={isHistoryItemSelected(historyEmail)}
                $isDarkMode={isDarkMode}
                style={{
                  cursor: isImpersonating ? 'default' : 'pointer',
                  opacity:
                    isImpersonating && currentUserEmail !== historyEmail
                      ? 0.5
                      : 1
                }}
              >
                <ImpersonationHistoryEmail
                  $isSelected={isHistoryItemSelected(historyEmail)}
                  $isDarkMode={isDarkMode}
                >
                  {historyEmail}
                </ImpersonationHistoryEmail>
                <ImpersonationHistoryDeleteButton
                  onClick={handleRemoveHistoryItem(historyEmail)}
                  title="Remove from history"
                  aria-label="Remove from history"
                >
                  <DeleteOutline style={{ fontSize: '16px' }} />
                </ImpersonationHistoryDeleteButton>
              </ImpersonationHistoryItem>
            ))}
          </ImpersonationHistoryList>
        </ImpersonationHistoryContainer>
      )}
    </>
  )
}

export default memo(withCommonSnapshotsOnly(ImpersonateUser))
